import { WarehouseDirectoryItem } from '@/console/api/types'
import { POLICY_LABELS } from './policyForm'

/**
 * LOGISPRD-15602 — 왼쪽 `창고 목록` 의 순수 로직(검색 · 상태 필터 · 정렬 · 누락 속성 라벨).
 * 화면은 이 모듈이 만든 배열만 그린다.
 */

export type StatusFilter = 'all' | 'active' | 'inactive'

export const STATUS_FILTERS: readonly { key: StatusFilter; label: string }[] = [
  { key: 'all', label: '전체' },
  { key: 'active', label: '활성' },
  { key: 'inactive', label: '비활성' },
]

/** 코드·이름 부분일치, 대소문자 무시. 빈 검색어는 전부 통과. */
export const matchesQuery = (item: WarehouseDirectoryItem, query: string): boolean => {
  const q = query.trim().toLowerCase()
  if (q.length === 0) {
    return true
  }
  return item.warehouseCode.toLowerCase().includes(q) || (item.warehouseName ?? '').toLowerCase().includes(q)
}

const matchesStatus = (item: WarehouseDirectoryItem, filter: StatusFilter): boolean =>
  filter === 'all' || (filter === 'active' ? item.active : !item.active)

/** 활성 먼저, 그 안에서 코드 오름차순. 원본 배열은 건드리지 않는다. */
export const sortDirectory = (items: readonly WarehouseDirectoryItem[]): WarehouseDirectoryItem[] =>
  [...items].sort((a, b) => {
    if (a.active !== b.active) {
      return a.active ? -1 : 1
    }
    return a.warehouseCode.localeCompare(b.warehouseCode)
  })

/** 세그먼트 옆 개수 — 검색어를 적용한 뒤의 개수다(필터를 바꾸면 몇 곳이 보일지 미리 알 수 있게). */
export const countByStatus = (items: readonly WarehouseDirectoryItem[], query: string): Record<StatusFilter, number> => {
  const searched = items.filter((item) => matchesQuery(item, query))
  const active = searched.filter((item) => item.active).length
  return { all: searched.length, active, inactive: searched.length - active }
}

export const visibleDirectory = (items: readonly WarehouseDirectoryItem[], query: string, filter: StatusFilter): WarehouseDirectoryItem[] =>
  sortDirectory(items.filter((item) => matchesQuery(item, query) && matchesStatus(item, filter)))

/* ───────── 활성화 누락 속성 ───────── */

/** 이 화면에서 채울 수 없는 창고 속성(마스터 원장). 정책 type 은 아래 카드에서 채운다. */
const ATTRIBUTE_LABELS: Record<string, string> = {
  MODULE: '모듈',
  WH_NM: '창고명',
  ADDR1: '주소1',
  ADDR2: '주소2',
  ZIPCODE: '우편번호',
}

export type MissingAttribute = { key: string; label: string; editableHere: boolean }

/**
 * BE `missingAttributes` 한 값 → 화면 라벨. 정책 type 은 카드 라벨로 바꾸고 이 화면에서 채울 수 있다고 표시한다.
 * 모르는 값은 원문 그대로, 편집 불가로 둔다(채울 방법을 이 화면이 모른다).
 */
export const describeMissing = (key: string): MissingAttribute => {
  if (key in POLICY_LABELS) {
    return { key, label: POLICY_LABELS[key as keyof typeof POLICY_LABELS], editableHere: true }
  }
  return { key, label: ATTRIBUTE_LABELS[key] ?? key, editableHere: false }
}
